import { Spin, Tabs } from 'antd';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setHeader, setRoutes } from 'src/core/ui/shell';
import { OrganizationItemStore } from 'src/stores/organizations-store';
import { container } from 'src/inversify.config';
import { useIdentity } from 'src/core/services/authentication';
import OrganizationBackupTab from './components/organization-backup-tab';
import OrganizationGeneralPropertyEditCard from './components/organization-general-properties-card';
import OrganizationMembersTab from './components/organization-members-tab';

import 'src/assets/cards-detail-view-layout.less';
import { useParams } from 'react-router-dom';
import { PermissionsConstants } from 'src/common/types';
import { useCurrentUserTenantPermissionsFilter } from 'src/common/permission-hooks';

const permissions = [
  PermissionsConstants.CreatePartnerOrganization,
  PermissionsConstants.CreateOrganization,
  PermissionsConstants.DeletePartnerOrganization,
  PermissionsConstants.DeleteOrganization,
  PermissionsConstants.BlockOrganization,
  PermissionsConstants.ManageOrganizationInfo,
  PermissionsConstants.ManagePartnerOrganization,
];

const OrganizationDetailPage: FC = () => {
  const identity = useIdentity();
  const params = useParams();
  const { t } = useTranslation();
  let organizationId = params.id!;

  const [userPermissionsSet, checkingPermissions] = useCurrentUserTenantPermissionsFilter(identity, permissions);

  const canCreateOrg = userPermissionsSet.has(PermissionsConstants.CreateOrganization);
  const canEditOrg = userPermissionsSet.has(PermissionsConstants.ManageOrganizationInfo);
  const canBlockOrg = userPermissionsSet.has(PermissionsConstants.BlockOrganization);
  const canDeleteOrg = userPermissionsSet.has(PermissionsConstants.DeleteOrganization);
  const canManagePartnerOrg = userPermissionsSet.has(PermissionsConstants.ManagePartnerOrganization);

  const organizationStore = container.get(OrganizationItemStore);
  const organizationState = organizationStore.state;
  const [organizationDisplayName, setOrganizationDisplayName] = useState('');

  useEffect(() => {
    organizationStore.load(organizationId).then(() => {
      setOrganizationDisplayName(organizationState.item.value.displayName);
    });
    const mainContainer = document.querySelector('main.ant-layout-content');
    if (mainContainer) {
      mainContainer.classList.add('edit-mode');
    }
    return () => {
      mainContainer && mainContainer.classList.remove('edit-mode');
    };
  }, [organizationId, organizationStore]); // eslint-disable-line react-hooks/exhaustive-deps

  if (organizationState.item.value) {
    setHeader(t('OrganizationParam').replace('{0}', organizationState.item.displayName.value));
    setRoutes([
      { path: '', breadcrumbName: t('Home') },
      { path: 'organizations', breadcrumbName: t('Organizations') },
      { path: organizationState.item.id.value, breadcrumbName: organizationState.item.displayName.value },
    ]);
  }

  return (
    <div className="entity-details_card-grid-container" style={{ height: 'auto' }}>
      <Spin spinning={organizationState.isBusy.get() || checkingPermissions} size="large">
        <div className="entity-details_card-grid-container__form-container">
          <div className="entity-details_card-grid-container__left-container" style={{ width: '30%', minWidth: 200, borderRadius: 8 }}>
            <OrganizationGeneralPropertyEditCard
              organizationId={organizationId}
              canEditOrg={canEditOrg}
              canBlockOrg={canBlockOrg}
              canCreateOrg={canCreateOrg}
              canDeleteOrg={canDeleteOrg}
              organizationDisplayName={organizationDisplayName}
            />
          </div>
          <div className="entity-details_card-grid-container__right-container" style={{ width: '70%', minWidth: 200, borderRadius: 8 }}>
            <Tabs defaultActiveKey="1" type="card">
              <Tabs.TabPane tab={t('Members')} key="1">
                <div className="tab-container">
                  <OrganizationMembersTab />
                </div>
              </Tabs.TabPane>
              {canManagePartnerOrg && (
                <Tabs.TabPane tab={t('Backups')} key="2">
                  <div className="tab-container">
                    <OrganizationBackupTab organizationId={organizationId} />
                  </div>
                </Tabs.TabPane>
              )}
            </Tabs>
          </div>
        </div>
      </Spin>
    </div>
  );
};

export default OrganizationDetailPage;
